import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LANG_OPTIONS } from './LangBar';
import { useOUItems } from '@/hooks/useOUs';
import { OuTreeSelect } from '@/components/common/OuTree';

type OU = string;

export interface OUOverride { id: string; ou: OU; lang: string; userMsg: string; mgrMsg: string; }

interface OUPersonalizationSectionProps {
  overrides: OUOverride[];
  onAdd: () => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, field: keyof OUOverride, value: string) => void;
  showMgrField: boolean;
}

export function OUPersonalizationSection({ overrides, onAdd, onRemove, onUpdate, showMgrField }: OUPersonalizationSectionProps) {
  const { t } = useTranslation();
  const { data: ouItems = [] } = useOUItems();
  const [open, setOpen] = useState(false);
  const count = overrides.length;

  return (
    <div className="notif-ou-section">
      <button className="notif-ou-toggle" onClick={() => setOpen(previousOpen => !previousOpen)}>
        <i className="fas fa-flag notif-ou-icon" />
        <span className="notif-ou-label">{t('admin.notifOUPersonalization')}</span>
        {count > 0 && (
          <span className="notif-ou-count">
            {count} {count === 1 ? t('admin.ouPersonalizedSingular') : t('admin.ouPersonalizedPlural')}
          </span>
        )}
        <i className={`fas fa-chevron-down notif-ou-chevron${open ? ' open' : ''}`} />
      </button>
      {open && (
        <div className="notif-ou-body">
          <div className="notif-ou-list">
            {overrides.map(override => (
              <div key={override.id} className="notif-ou-item">
                <div className="notif-ou-item-head">
                  <div className="notif-ou-item-meta">
                    <div className={`notif-ou-chip notif-ou-chip--${override.ou}`}>
                      <OuTreeSelect
                        mode="single"
                        variant="dropdown"
                        items={ouItems}
                        value={override.ou ? [override.ou] : []}
                        onChange={ids => onUpdate(override.id, 'ou', ids[0] ?? '')}
                      />
                    </div>
                    <select className="notif-ou-lang-select" value={override.lang}
                      onChange={e => onUpdate(override.id, 'lang', e.target.value)}>
                      {LANG_OPTIONS.map(langOption => (
                        <option key={langOption.id} value={langOption.id}>{langOption.flag} {langOption.label}</option>
                      ))}
                    </select>
                  </div>
                  <button className="notif-ou-del-btn" onClick={() => onRemove(override.id)} title={t('common.delete')}>
                    <i className="fas fa-trash-alt" />
                  </button>
                </div>
                <div className="notif-ou-item-messages">
                  <div className="setting-item full-width">
                    <label>{t('admin.messageForUser')}</label>
                    <textarea className="message-textarea" rows={3}
                      value={override.userMsg}
                      onChange={e => onUpdate(override.id, 'userMsg', e.target.value)}
                      placeholder={t('admin.notifCustomUserMsgPlaceholder')} />
                    <div className="variables-hint">Variables: {'{nombre}'}, {'{count}'}, {'{fecha}'}</div>
                  </div>
                  {showMgrField && (
                    <div className="setting-item full-width">
                      <label>{t('admin.messageForManager')}</label>
                      <textarea className="message-textarea" rows={2}
                        value={override.mgrMsg}
                        onChange={e => onUpdate(override.id, 'mgrMsg', e.target.value)}
                        placeholder={t('admin.notifCustomMgrMsgPlaceholder')} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button className="notif-ou-add-btn" onClick={onAdd}>
            <i className="fas fa-plus" /> {t('admin.addOUPersonalization')}
          </button>
        </div>
      )}
    </div>
  );
}
